import React, { useState } from 'react';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Card } from '../../../components/Card/Card';
import { AvatarUpload } from '../../../components/AvatarUpload/AvatarUpload';
import { User, fullUserName, patchUser, UserEdit, deleteUser } from '../../../model/user';
import { KeyValueTable } from '../../../components/KeyValueTable/KeyValueTable';
import { UserForm } from '../UserForm/UserForm';
import { formatDateTime, readBytesUrl } from '../../../util/util';
import { handleError } from '../../../model/notification';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { t } from '../../../locales/translate';
import { ControlledModal } from '../../../components/Modal/ControlledModal';

interface Props {
  user: User;
  onUserEdit: (model: UserEdit) => Promise<void>;
  reloadActivities: () => void;
}

export function UserGeneralInfo({ user, reloadActivities }: Props): JSX.Element {
  const [editModal, setEditModal] = useState(false);
  const [loadingImageUpload, setLoadingImageUpload] = useState(false);
  const [errorImageUpload, setErrorImageUpload] = useState(false);

  async function handleOnChange(e?: File): Promise<void> {
    setLoadingImageUpload(true);
    try {
      if (!e) {
        await patchUser(user.id, {
          avatar: undefined,
        });
      } else {
        const bytes = await readBytesUrl(e);
        await patchUser(user.id, { avatar: bytes });
      }
      setLoadingImageUpload(false);
    } catch (error) {
      handleError(error);
      setErrorImageUpload(true);
      setLoadingImageUpload(false);
    }
  }

  const { confirm } = useConfirmation({
    title: t.actions.delete(t.model.user._name),
    message: t.general.confirmDelete(t.model.user._name),
    action: async () => await deleteUser(user.id),
  });

  return (
    <div>
      <Card
        className="user-details__general-info"
        title={t.general.generalInformation}
        elevation={1}
        actions={
          <>
            <PermissionButton onClick={() => setEditModal(true)} name="edit" model={user} permission="update" />
            <PermissionButton onClick={confirm} name="trash" model={user} permission="destroy" />
          </>
        }
      >
        <div className="user-details__avatar">
          <AvatarUpload
            image={user.avatar ? user.avatar.url : undefined}
            onChange={handleOnChange}
            shape="round"
            loading={loadingImageUpload}
            error={errorImageUpload}
          />
        </div>
        <div className="user-details__details">
          <h1 className="user-details__name">{fullUserName(user)}</h1>
          <KeyValueTable
            className="user-details__phone-email"
            displayEmpty={false}
            items={[
              { key: t.model.user.email, value: user.email },
              { key: t.model.user.phone, value: user.phone },
              { key: t.model.datetime.createdAt, value: formatDateTime(user.createdAt) },
            ]}
          />
        </div>
      </Card>
      <ControlledModal<UserEdit>
        open={editModal}
        model={{
          ...user,
          avatar: '',
        }}
        title={t.actions.edit(t.model.user._name)}
        onClose={() => setEditModal(false)}
        size="small"
      >
        {(controller) => <UserForm controller={controller} onSuccess={reloadActivities} />}
      </ControlledModal>
    </div>
  );
}
